import { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Authentication layout components
import CustomBasicLayout from "layouts/auth/components/CustomBasicLayout";

import AuthService from "services/auth-service";
import userService from "services/user-service";
import JwtService from "services/jwt-service";
import { AuthContext } from "context";

function Login() {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const [credentialsErros, setCredentialsError] = useState(null);

  const [inputs, setInputs] = useState({
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState({
    emailError: false,
    passwordError: false,
  });

  const changeHandler = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    const mailFormat = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;

    if (inputs.email.trim().length === 0 || !inputs.email.trim().match(mailFormat)) {
      setErrors({ ...errors, emailError: true });
      return;
    }
    if (inputs.password.trim().length < 6) {
      setErrors({ ...errors, passwordError: true });
      return;
    }

    const user = { username: inputs.email, password: inputs.password };

    try {
      const response = await AuthService.login(user);
      authContext.login(response.access_token);
      localStorage.setItem("userEmail", user.username);
      const userData = await userService.getUserByEmail(user.username);
      if (userData && userData.id) {
        localStorage.setItem("userId", userData.id);
      }
      
      // Admins and regular users both go to financial hub
      navigate("/financial-hub");
    } catch (res) {
      // Handle different types of errors
      if (res && res.error === "invalid_grant") {
        // Check if it's an account activation issue
        if (res.error_description && res.error_description.includes("disabled")) {
          setCredentialsError("Account is disabled. Please contact administrator.");
        } else {
          setCredentialsError("Invalid email or password. Please try again. If you haven't activated your account yet, please check your email for activation instructions.");
        }
      } else if (res && res.hasOwnProperty("message")) {
        setCredentialsError(res.message);
      } else if (res && res.errors && res.errors.length > 0) {
        setCredentialsError(res.errors[0].detail);
      } else {
        setCredentialsError("Login failed. Please check your credentials and try again.");
      }
    }
  };

  const inputStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 3,
      backgroundColor: "#f8f9fa",
      "& fieldset": { borderColor: "#e0e0e0" },
      "&:hover fieldset": { borderColor: "#465475" },
      "&.Mui-focused fieldset": { borderColor: "#465475", borderWidth: 2 },
    },
  };

  return (
    <CustomBasicLayout>
      <Card sx={{ maxWidth: 420, width: "100%", p: 4, borderRadius: 3 }}>
        {/* Logo/Icon */}
        <MDBox display="flex" flexDirection="column" alignItems="center" mb={3}>
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            width={80}
            height={80}
            borderRadius="50%"
            sx={{
              background: "linear-gradient(135deg, #5a6a94 0%, #465475 100%)",
              boxShadow: "0 4px 20px rgba(70, 84, 117, 0.3)",
              mb: 2,
            }}
          >
            <Icon sx={{ fontSize: 36, color: "white" }}>account_circle</Icon>
          </MDBox>
          <MDTypography variant="h4" fontWeight="bold" color="dark">
            Welcome Back
          </MDTypography>
          <MDTypography variant="body2" color="text" sx={{ mt: 0.5 }}>
            Sign in to continue to your account
          </MDTypography>
        </MDBox>

        {/* Form */}
        <MDBox component="form" onSubmit={submitHandler}>
          {/* Email */}
          <MDBox mb={2.5}>
            <MDTypography variant="button" fontWeight="medium" color="text" display="block" mb={1}>
              Email Address
            </MDTypography>
            <TextField
              fullWidth
              type="email"
              name="email"
              placeholder="you@example.com"
              value={inputs.email}
              onChange={changeHandler}
              error={errors.emailError}
              helperText={errors.emailError ? "Please enter a valid email" : ""}
              sx={inputStyles}
            />
          </MDBox>

          {/* Password */}
          <MDBox mb={2}>
            <MDTypography variant="button" fontWeight="medium" color="text" display="block" mb={1}>
              Password
            </MDTypography>
            <TextField
              fullWidth
              type="password"
              name="password"
              placeholder="Enter your password"
              value={inputs.password}
              onChange={changeHandler}
              error={errors.passwordError}
              helperText={errors.passwordError ? "Password must be at least 6 characters" : ""}
              sx={inputStyles}
            />
          </MDBox>

          {/* Forgot Password Link */}
          <MDBox display="flex" justifyContent="flex-end" mb={2}>
            <MDTypography
              component={Link}
              to="/forgot-password"
              variant="button"
              color="info"
              fontWeight="medium"
            >
              Forgot password?
            </MDTypography>
          </MDBox>

          {/* Error Message */}
          {credentialsErros && (
            <MDBox mb={2} p={1.5} borderRadius={2} sx={{ backgroundColor: "#fee2e2" }}>
              <MDTypography variant="caption" color="error" fontWeight="medium">
                {credentialsErros}
              </MDTypography>
            </MDBox>
          )}

          {/* Sign In Button */}
          <MDButton
            variant="gradient"
            color="info"
            fullWidth
            size="large"
            type="submit"
            sx={{
              borderRadius: 3,
              py: 1.5,
              textTransform: "none",
              fontSize: "1rem",
            }}
          >
            Sign In
          </MDButton>

          {/* Sign Up Link */}
          <MDBox mt={3} textAlign="center">
            <MDTypography variant="button" color="text">
              Don&apos;t have an account?{" "}
              <MDTypography
                component={Link}
                to="/register"
                variant="button"
                color="info"
                fontWeight="medium"
              >
                Sign up
              </MDTypography>
            </MDTypography>
          </MDBox>
        </MDBox>
      </Card>
    </CustomBasicLayout>
  );
}

export default Login;